import ErrorFallback from "@/components/error-fallback"
import { reloadOnceForStaleChunk } from "@/lib/utils/chunk-errors"
import { Component, ErrorInfo, ReactNode } from "react"

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  private reset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (reloadOnceForStaleChunk(error)) {
      return
    }

    if (import.meta.env.DEV) {
      console.error(error, errorInfo)
    }

    void import("@/lib/utils/sentry-client")
      .then(({ captureException }) => {
        captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
        })
      })
      .catch(() => {
        if (import.meta.env.DEV) {
          console.error("[+Bake Cafe] Failed to load Sentry client")
        }
      })
  }

  public render() {
    if (this.state.hasError && this.state.error) {
      const structuredError = {
        message: this.state.error.message,
        stack: this.state.error.stack,
        name: this.state.error.name,
        cause: this.state.error.cause,
      }

      if (import.meta.env.DEV) {
        console.error(structuredError)
      }

      if (this.props.fallback) {
        return this.props.fallback
      }

      return <ErrorFallback error={this.state.error} reset={this.reset} />
    }

    return this.props.children
  }
}

export default ErrorBoundary
